import React, { useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Divider, Form, Icon, Input, notification, Row, Spin, Table, Tag } from 'antd';
import CustomAutoComplete from '../CustomAutoComplete';
import api from '../../helpers/api';
import translate, { translateFormat } from '../../i18n/translate';

const STATUS_CONFIG = {
  pending: { label: translate('product.batchClientUpdate.status.pending'), color: 'blue' },
  success: { label: translate('product.batchClientUpdate.status.success'), color: 'green' },
  warning: { label: translate('product.batchClientUpdate.status.warning'), color: 'orange' },
  error: { label: translate('product.batchClientUpdate.status.error'), color: 'red' },
};

function compareText(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });
}

function resolveFallbackStatusFromDetails(details) {
  const failed = Number(details?.failed || 0);
  const warning = Number(details?.warning || 0);
  const success = Number(details?.success || 0);

  if (failed > 0) return 'error';
  if (warning > 0) return 'warning';
  if (success > 0) return 'success';
  return 'error';
}

function parseSerials(input) {
  if (!input) {
    return { serials: [], duplicated: 0 };
  }

  const parts = input
    .split(/[\s,;]+/g)
    .map((value) => value.trim().toUpperCase())
    .filter(Boolean);

  const unique = Array.from(new Set(parts));

  return {
    serials: unique,
    duplicated: parts.length - unique.length,
  };
}

function parseNotFoundSerialsFromMessage(message) {
  if (!message) return [];
  const match = message.match(/(?:não encontrados?|serials?\s+not\s+found)[:\s]+([^\n.]+)/i);
  if (!match || !match[1]) return [];
  return match[1].split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
}

function localizeBatchApiMessage(message) {
  if (!message) {
    return {
      uiMessage: translate('product.batchClientUpdate.notifications.error'),
      notFoundSerials: [],
    };
  }

  const notFoundSerials = parseNotFoundSerialsFromMessage(message);
  if (notFoundSerials.length) {
    return {
      uiMessage: translateFormat('product.batchClientUpdate.notifications.serialsNotFoundList', notFoundSerials.join(', ')),
      notFoundSerials,
    };
  }

  if (/client_id.*(invalid|inválido)|serials?.*(empty|vazio)/i.test(message)) {
    return {
      uiMessage: translate('product.batchClientUpdate.notifications.invalidClientOrSerials'),
      notFoundSerials: [],
    };
  }

  return {
    uiMessage: message,
    notFoundSerials: [],
  };
}

function localizeResultItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    const msg = item && item.message ? item.message : '';
    if (/serial\s+not\s+found/i.test(msg)) {
      return {
        ...item,
        status: 'error',
        message: translate('product.batchClientUpdate.notifications.serialNotFound'),
      };
    }
    if (/serial\s+already\s+belongs\s+to\s+this\s+client/i.test(msg)) {
      return {
        ...item,
        status: 'warning',
        message: translate('product.batchClientUpdate.notifications.alreadyInThisClient'),
      };
    }
    if (/update\s+canceled\s+because\s+request\s+contains\s+invalid\s+serials?/i.test(msg)) {
      return {
        ...item,
        status: 'error',
        message: translate('product.batchClientUpdate.notifications.invalidSerialsInRequest'),
      };
    }
    if (/client\s+updated\s+successfully/i.test(msg)) {
      return {
        ...item,
        status: 'success',
        message: translate('product.batchClientUpdate.notifications.updatedSuccessfully'),
      };
    }
    return item;
  });
}

function getBatchSuccessMessage(response) {
  const rawMessage = response && typeof response.message === 'string' ? response.message.trim() : '';
  if (!rawMessage) {
    return translate('product.batchClientUpdate.notifications.success');
  }

  // Backend sometimes returns malformed success text like "undefined items were updated".
  if (/undefined\s+items?\s+were\s+updated/i.test(rawMessage)) {
    return translate('product.batchClientUpdate.notifications.success');
  }

  return rawMessage;
}

function ProductBatchClientUpdate() {
  const [clientId, setClientId] = useState(null);
  const [serialInput, setSerialInput] = useState('');
  const [serials, setSerials] = useState([]);
  const [tableSearch, setTableSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultItems, setResultItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [lastDuplicates, setLastDuplicates] = useState(0);
  const [blockingError, setBlockingError] = useState(null);
  const serialInputRef = useRef(null);

  const parsed = useMemo(() => parseSerials(serialInput), [serialInput]);
  const serialsToDisplay = useMemo(() => serials, [serials]);
  const pendingRows = useMemo(
    () =>
      serialsToDisplay.map((serial, index) => ({
        key: `${serial}-${index}`,
        serial,
        status: 'pending',
        message: translate('product.batchClientUpdate.table.emptyMessage'),
        canRemove: true,
      })),
    [serialsToDisplay],
  );

  const tableData = useMemo(() => {
    if (resultItems.length) {
      return resultItems.map((item, index) => {
        const canRemove = item.status === 'error' || item.status === 'pending' || item.status === 'warning';
        return {
          ...item,
          key: `${item.serial || 'serial'}-${index}`,
          status: item.status || (item.success ? 'success' : 'error'),
          canRemove,
          removeButton: canRemove ? (
            <Button
              shape="circle"
              icon="close"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                removeSerial(item.serial);
              }}
            />
          ) : null,
        };
      });
    }
    return pendingRows.map((row) => ({
      ...row,
      removeButton: (
        <Button
          shape="circle"
          icon="close"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            removeSerial(row.serial);
          }}
        />
      ),
    }));
  }, [resultItems, pendingRows]);

  const filteredTableData = useMemo(() => {
    const search = tableSearch.trim().toUpperCase();
    if (!search) return tableData;

    return tableData.filter((item) => {
      const serial = String(item.serial || '').toUpperCase();
      const message = String(item.message || '').toUpperCase();
      const statusLabel = String((STATUS_CONFIG[item.status] || STATUS_CONFIG.pending).label || '').toUpperCase();
      return serial.includes(search) || message.includes(search) || statusLabel.includes(search);
    });
  }, [tableData, tableSearch]);

  const columns = useMemo(() => [
    {
      title: translate('product.batchClientUpdate.table.serial'),
      dataIndex: 'serial',
      key: 'serial',
      sorter: (a, b) => compareText(a.serial, b.serial),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: translate('product.batchClientUpdate.table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
      sorter: (a, b) => {
        const statusA = (STATUS_CONFIG[a.status] || STATUS_CONFIG.pending).label;
        const statusB = (STATUS_CONFIG[b.status] || STATUS_CONFIG.pending).label;
        return compareText(statusA, statusB);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: translate('product.batchClientUpdate.table.message'),
      dataIndex: 'message',
      key: 'message',
      sorter: (a, b) => compareText(a.message, b.message),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: '',
      dataIndex: 'canRemove',
      key: 'canRemove',
      width: 70,
      render: (canRemove, record) => (canRemove ? record.removeButton : null),
    },
  ], []);

  async function onSubmit() {
    const serialsToSubmit = serials;

    if (!clientId) {
      notification.warning({ message: translate('product.batchClientUpdate.notifications.selectClient') });
      return;
    }

    if (!serialsToSubmit.length) {
      notification.warning({ message: translate('product.batchClientUpdate.notifications.requireSerial') });
      return;
    }

    setBlockingError(null);
    setLoading(true);
    try {
      const { data: response } = await api.put('/product/bulk/client', {
        client_id: clientId,
        serials: serialsToSubmit,
      });

      const result = response && response.details
        ? response.details
        : (response && response.result ? response.result : {});
      const responseItems = Array.isArray(result.items) ? result.items : [];
      const fallbackStatus = resolveFallbackStatusFromDetails(result);
      const items = responseItems.length
        ? localizeResultItems(responseItems)
        : serialsToSubmit.map((serial) => ({
          serial,
          status: fallbackStatus,
          message:
            fallbackStatus === 'error'
              ? translate('product.batchClientUpdate.notifications.error')
              : fallbackStatus === 'warning'
                ? translate('product.batchClientUpdate.notifications.alreadyInThisClient')
                : translate('product.batchClientUpdate.notifications.updatedSuccessfully'),
        }));

      setResultItems(items);
      setSummary({
        total: Number(result.total || serialsToSubmit.length),
        success: Number(result.success || 0),
        warning: Number(result.warning || 0),
        failed: Number(result.failed || 0),
      });

      if (fallbackStatus === 'error') {
        notification.error({
          message: translate('product.batchClientUpdate.notifications.error'),
        });
      } else if (fallbackStatus === 'warning') {
        notification.warning({
          message: translate('product.batchClientUpdate.notifications.alreadyInThisClient'),
        });
      } else {
        notification.success({
          message: getBatchSuccessMessage(response),
        });
      }
    } catch (error) {
      const errorData = error?.response?.data || {};
      const rawMessage = errorData.message || translate('product.batchClientUpdate.notifications.error');
      const mappedError = localizeBatchApiMessage(rawMessage);
      notification.error({ message: mappedError.uiMessage });

      if (Array.isArray(errorData.result?.items) && errorData.result.items.length) {
        setResultItems(localizeResultItems(errorData.result.items));
        setSummary({
          total: Number(errorData.result.total || serialsToSubmit.length),
          success: Number(errorData.result.success || 0),
          warning: Number(errorData.result.warning || 0),
          failed: Number(errorData.result.failed || serialsToSubmit.length),
        });
      } else {
        const notFoundSerials = mappedError.notFoundSerials;

        const items = serialsToSubmit.map((serial) => {
          if (!notFoundSerials.length || notFoundSerials.includes(serial.toUpperCase())) {
            return { serial, status: 'error', message: translate('product.batchClientUpdate.notifications.serialNotFound') };
          }
          return { serial, status: 'pending', message: translate('product.batchClientUpdate.table.emptyMessage') };
        });

        const failed = items.filter((i) => i.status === 'error').length;
        setResultItems(items);
        setSummary({
          total: serialsToSubmit.length,
          success: 0,
          warning: 0,
          failed,
        });
        setBlockingError(translateFormat('product.batchClientUpdate.alerts.noneUpdatedDescription', failed));
      }
    } finally {
      setLoading(false);
    }
  }

  function onAddSerials() {
    if (!parsed.serials.length) {
      notification.warning({ key: 'empty-serial', message: translate('product.batchClientUpdate.notifications.addSerialHint') });
      return;
    }

    const merged = Array.from(new Set([...parsed.serials, ...serials]));
    const removedAsDuplicate = serials.length + parsed.serials.length - merged.length;

    setSerials(merged);
    setLastDuplicates(removedAsDuplicate + parsed.duplicated);
    setSerialInput('');
    setResultItems([]);
    setSummary(null);
    if (serialInputRef.current) {
      serialInputRef.current.focus();
    }
  }

  function removeSerial(serial) {
    setSerials((prev) => prev.filter((value) => value !== serial));
    setResultItems([]);
    setSummary(null);
    setBlockingError(null);
  }

  function clearList() {
    setSerials([]);
    setResultItems([]);
    setSummary(null);
    setBlockingError(null);
    setLastDuplicates(0);
  }

  function editSerial(serial) {
    removeSerial(serial);
    setSerialInput(serial);
    if (serialInputRef.current) {
      serialInputRef.current.focus();
    }
  }

  return (
    <Card bordered={false} style={{ marginTop: 3, width: '100%' }}>
      <Spin spinning={loading} tip={translate('product.batchClientUpdate.loading.processing')}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 14 }}
          message={translate('product.batchClientUpdate.guide.title')}
          description={(
            <ol style={{ paddingLeft: 18, marginBottom: 0 }}>
              <li>{translate('product.batchClientUpdate.guide.step1')}</li>
              <li>{translate('product.batchClientUpdate.guide.step2')}</li>
              <li>{translate('product.batchClientUpdate.guide.step3')}</li>
            </ol>
          )}
        />
        <Form layout="vertical">
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 12 }}>{translate('product.batchClientUpdate.labels.targetClient')}</div>
              <CustomAutoComplete
                searchValue="id"
                searchKey="name"
                path="/client"
                placeholder={translate('product.batchClientUpdate.placeholders.client')}
                onSelect={(value) => setClientId(value)}
                value={clientId}
              />
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 12 }}>{translate('product.batchClientUpdate.labels.serial')}</div>
              <Input.Group compact style={{ display: 'flex' }}>
                <Input
                  ref={serialInputRef}
                  value={serialInput}
                  onChange={(event) => setSerialInput(event.target.value)}
                  onPressEnter={onAddSerials}
                  placeholder={translate('product.batchClientUpdate.placeholders.serialExample')}
                  autoFocus
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  onClick={onAddSerials}
                  disabled={!parsed.serials.length}
                  style={{ height: 32 }}
                >
                  <Icon type="plus" /> {translate('product.batchClientUpdate.actions.add')}
                </Button>
              </Input.Group>
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginTop: 4 }}>
                {translate('product.batchClientUpdate.hints.enterToAdd')}
              </div>
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginTop: 2 }}>
                {translate('product.batchClientUpdate.hints.bulkPaste')}
              </div>
              {!!parsed.serials.length && (
                <div style={{ marginTop: 8 }}>
                  {parsed.serials.slice(0, 20).map((serial) => (
                    <Tag key={`preview-${serial}`} color="geekblue" style={{ marginBottom: 6 }}>
                      {serial}
                    </Tag>
                  ))}
                  {parsed.serials.length > 20 && (
                    <Tag color="default">+{parsed.serials.length - 20}</Tag>
                  )}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <Tag color="cyan">{translate('product.batchClientUpdate.summary.readyToSend')} {serialsToDisplay.length}</Tag>
                {!!parsed.serials.length && <Tag color="purple">{translate('product.batchClientUpdate.summary.inInput')} {parsed.serials.length}</Tag>}
              </div>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 10 }}>
            <Col>
              <Tag color="blue">
                <Icon type="unordered-list" /> {translate('product.batchClientUpdate.summary.inList')} {serialsToDisplay.length}
              </Tag>
              <Tag color={lastDuplicates ? 'orange' : 'green'}>
                <Icon type="disconnect" /> {translate('product.batchClientUpdate.summary.removedDuplicates')} {lastDuplicates}
              </Tag>
              <Button
                size="small"
                style={{ marginLeft: 8 }}
                disabled={!serials.length}
                onClick={clearList}
              >
                <Icon type="delete" /> {translate('product.batchClientUpdate.actions.clearList')}
              </Button>
            </Col>
          </Row>

          <Divider style={{ margin: '10px 0 16px' }} />

          <Row gutter={16}>
            <Col>
              <Button
                onClick={onSubmit}
                loading={loading}
                disabled={!clientId || !serialsToDisplay.length}
              >
                <Icon type="sync" /> {translate('product.batchClientUpdate.actions.sendList')}
              </Button>
            </Col>
          </Row>
        </Form>

        {blockingError && (
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Alert
                type="error"
                showIcon
                message={translate('product.batchClientUpdate.alerts.noneUpdatedTitle')}
                description={blockingError}
              />
            </Col>
          </Row>
        )}

        {summary && (
          <Row style={{ marginTop: 20, marginBottom: 8 }}>
            <Col>
              <Tag color="blue">{translate('product.batchClientUpdate.summary.total')} {summary.total}</Tag>
              <Tag color="green">{translate('product.batchClientUpdate.summary.updated')} {summary.success}</Tag>
              <Tag color="orange">{translate('product.batchClientUpdate.summary.alreadyInClient')} {summary.warning}</Tag>
              <Tag color="red">{translate('product.batchClientUpdate.summary.notFound')} {summary.failed}</Tag>
            </Col>
          </Row>
        )}

        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            <Input
              value={tableSearch}
              onChange={(event) => setTableSearch(event.target.value)}
              placeholder={translate('product.batchClientUpdate.placeholders.searchTable')}
              prefix={<Icon type="search" />}
              allowClear
              style={{ maxWidth: 360, marginBottom: 10 }}
            />
            <Table
              size="small"
              columns={columns}
              dataSource={filteredTableData}
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText: translate('product.batchClientUpdate.table.emptyList'),
              }}
              onRow={(record) => ({
                onClick: () => {
                  if (record.canRemove) editSerial(record.serial);
                },
                style: record.canRemove ? { cursor: 'pointer' } : {},
              })}
            />
          </Col>
        </Row>
      </Spin>
    </Card>
  );
}

export default ProductBatchClientUpdate;
